import React, { useContext, useEffect } from 'react';
import { useLoaderData, useNavigate } from 'react-router-dom';

import Location from '@r6digital/rapidstor-types/react/entities/Location';

import store from '@/store';
import { setReservationData, setUnitData, setUpsell } from '@/store/bookingSlice';

import BookUnit from '@/journey-based-listing/screens/book-unit';
import BookUnitMap from '@/journey-based-listing/screens/book-unit-map';
import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';
import { prepareBookingNavigation } from '@/journey-based-listing/utils/prepareBookingNavigation';

/**
 * Runtime guard for the /book-unit route.
 * When usingDealSelectorVariant is enabled, prepares store data and redirects to /customise-booking.
 * When disabled, renders the standard BookUnit or BookUnitMap component.
 */
export default function BookUnitGuard() {
    const location = useLoaderData() as Location;
    const navigate = useNavigate();
    const { unitData, reservationData, dealSelectorVariant } = useContext(JourneyContext);

    useEffect(() => {
        if (!dealSelectorVariant) return;

        if (!unitData) {
            navigate('/');
            return;
        }

        const result = prepareBookingNavigation(
            unitData.descriptor,
            unitData.descriptors,
            location,
            reservationData ?? undefined,
            dealSelectorVariant,
        );

        // if there's no unit-types, we can't proceed with the booking, so navigate back to the listing page
        if (!result) {
            navigate('/');
            return;
        }

        const { reservationData: newReservationData, upsellDescriptor } = result;

        store.dispatch(setUnitData(unitData));
        store.dispatch(setReservationData(newReservationData));
        store.dispatch(setUpsell(upsellDescriptor ?? null));

        navigate('/customise-booking', { replace: true });
    }, [dealSelectorVariant, unitData, reservationData, location, navigate]);

    if (dealSelectorVariant) {
        return null;
    }

    const bookUnitStandard = window.rapidstor_settings.app_config.pages.bookUnit === 'standard';
    return bookUnitStandard ? <BookUnit /> : <BookUnitMap />;
}
